import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { postLuckyDrawApi } from '@flipster/apis/v1/campaign';
import { PostLuckyDrawResponse } from '@flipster/apis/v1/campaign/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { center, grid, vstack } from '@flipster/styled-system/patterns';
import { ServerError } from '@flipster/utils/constants/server-error';

import { abTestBunja } from '../../shared/ab-test';
import { authBunja } from '../../shared/auth/state';
import { ImgOrLottie } from '../assets';
import { LuckyDrawItemFragment, LuckyDrawPrizeItemFragment } from './query.generated';
import { luckyDrawBunja } from './state';

interface Props {
  isRunning: boolean;
  prizes: LuckyDrawPrizeItemFragment[];
  luckyDrawLayout?: LuckyDrawItemFragment;
  focusedIndex: number;
  rewardPrize?: LuckyDrawPrizeItemFragment;
}

export const SpinMachine = ({ isRunning, prizes, luckyDrawLayout, focusedIndex, rewardPrize }: Props) => {
  // NXN의 그리드라고 가정하고 그리드 컬럼 수를 계산
  const gridColumns = Math.ceil(Math.sqrt(prizes?.length ?? 9));

  if (!luckyDrawLayout) return null;
  return (
    <div className={css({ pos: 'relative', w: { tabletDown: '100%' } })}>
      <div
        className={center({
          gap: 10,
          p: {
            base: 6,
            tablet: 12,
          },
          m: { tablet: 80 },
          w: { base: '100%', tablet: 500 },
          borderWidth: { base: '4px', tablet: '0' },
          borderStyle: { base: 'solid', tablet: 'none' },
          borderColor: 'border.default.level-3',
          bgColor: 'background.default.level-0',
          rounded: 16,
        })}
      >
        {!isRunning && rewardPrize ? (
          <div className={vstack({ gap: 8, textAlign: 'center', whiteSpace: 'pre-line' })}>
            <ImgOrLottie
              url={rewardPrize.image?.url}
              alt={rewardPrize.name ?? 'prize'}
              css={{ w: { base: 140, tablet: 200 } }}
            />
            <p
              className={css({
                textStyle: 'Title/S',
                color: 'content.default.level-5',
                mt: { base: 8, tablet: 16 },
                whiteSpace: 'pre-line',
                mb: 4,
              })}
            >
              {rewardPrize.resultMessage || (
                <FormattedMessage
                  id='Competition.r251217.Congratulations_prize_is_yours_multiline'
                  values={{ prize: rewardPrize.name }}
                />
              )}
            </p>
          </div>
        ) : (
          <ul className={grid({ gap: 8, w: '100%' })} style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
            {prizes?.map((prize, i) => {
              const focused = focusedIndex === i;
              return (
                <li
                  className={css(
                    center.raw({
                      p: 12,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'transparent',
                      bgColor: 'background.default.level-2',
                      rounded: 8,
                    }),
                    focused && {
                      bgColor: 'background.accent.primary.tint',
                      borderColor: 'border.accent.promotion',
                    },
                  )}
                  key={prize?.id}
                >
                  <ImgOrLottie
                    url={prize?.image?.url}
                    alt={prize?.name ?? 'prize'}
                    css={{ w: '100%', aspectRatio: '1' }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {luckyDrawLayout?.skin?.url && (
        <ImgOrLottie
          url={luckyDrawLayout.skin.url}
          alt='spin-machine-skin'
          css={{ pos: 'absolute', inset: 0, w: '100%', display: { tabletDown: 'none' } }}
        />
      )}
    </div>
  );
};

/** 1 ~ numberOfItems까지 셔플해서 랜덤으로 순서 반환 */
function getShuffledNumbers(numberOfItems: number) {
  const numbers = Array.from({ length: numberOfItems }, (_, i) => i + 1);
  for (let i = numbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
  }
  return numbers;
}

interface SpinState {
  phase: 'accel' | 'plateau' | 'final';
  lap: number;
  stepIdx: number;
  sequence: number[];
  nextTickAt: number;
  pendingFinal: boolean;
  rewardPrizeIndex: number | null;
  rewardPrizeId: string | null;
  newTokenCount: number | null;
}

const animationIntervals = {
  maxInterval: 150,
  minInterval: 100,
  endInterval: 500,
  revealDelay: 500,
} as const;

export const useSpinMachine = (slug: string) => {
  const router = useRouter();
  const rafRef = useRef<number | null>(null);
  const stateRef = useRef<SpinState>({
    phase: 'accel',
    lap: 0,
    stepIdx: 0,
    sequence: [],
    nextTickAt: 0,
    pendingFinal: false,
    rewardPrizeIndex: null,
    rewardPrizeId: null,
    newTokenCount: null,
  });

  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { luckyDrawsLayout, luckyDrawsAtom, setNewDrawResultAtom } = useBunja(luckyDrawBunja);
  const setNewDrawResult = useSetAtom(setNewDrawResultAtom);
  const luckyDraws = useAtomValue(luckyDrawsAtom);
  const { prexInstallationId } = useBunja(abTestBunja);
  const [isRunning, setIsRunning] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [rewardPrizeId, setRewardPrizeId] = useState<string | null>(null);

  const luckyDrawLayout = useMemo(() => luckyDrawsLayout?.find((l) => l.slug === slug), [slug, luckyDrawsLayout]);
  const luckyDraw = useMemo(() => luckyDraws?.find((l) => l?.id === slug), [slug, luckyDraws]);
  const hasToken = !!luckyDraw?.token;
  const prizes = luckyDrawLayout?.prizesCollection?.items as LuckyDrawPrizeItemFragment[];
  const tokenName = luckyDrawLayout?.tokenIcon?.title ?? 'unknown';
  const rewardPrize = useMemo(() => prizes?.find((prize) => prize.id === rewardPrizeId), [prizes, rewardPrizeId]);

  const computeInterval = useCallback(
    (phase: SpinState['phase'], stepIdx: number) => {
      const { maxInterval, minInterval, endInterval } = animationIntervals;
      const numberOfItems = prizes?.length ?? 0;

      if (phase === 'plateau') return minInterval;
      const t = Math.min(1, Math.max(0, (stepIdx + 1) / numberOfItems));
      if (phase === 'accel') {
        const eased = t * t * t;
        return maxInterval + (minInterval - maxInterval) * eased;
      }
      const eased = 1 - Math.pow(1 - t, 3);
      return minInterval + (endInterval - minInterval) * eased;
    },
    [prizes],
  );

  const stop = useCallback(() => {
    setIsRunning(false);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (stateRef.current.rewardPrizeId != null && stateRef.current.newTokenCount != null) {
      setNewDrawResult({ slug, token: stateRef.current.newTokenCount, prizeId: stateRef.current.rewardPrizeId });
    }
  }, [slug, setNewDrawResult]);

  const setWinner = useCallback(
    (data: PostLuckyDrawResponse) => {
      if (!prizes) return;
      stateRef.current.rewardPrizeIndex = prizes.findIndex((prize) => prize.id === data.prizeId);
      stateRef.current.rewardPrizeId = data.prizeId;
      stateRef.current.newTokenCount = data.token;
      stateRef.current.pendingFinal = true; // 다음 바퀴를 final로 전환
    },
    [prizes],
  );

  // 회전 시작
  const start = useCallback(() => {
    if (!prizes) return;
    const numberOfItems = prizes.length;
    const state = stateRef.current;
    // 상태 초기화
    state.phase = 'accel';
    state.lap = 0;
    state.stepIdx = 0;
    state.pendingFinal = false;
    state.rewardPrizeIndex = null;
    state.rewardPrizeId = null;
    state.newTokenCount = null;
    state.sequence = getShuffledNumbers(numberOfItems);
    state.nextTickAt = performance.now();

    // 시작 포커스
    const firstIndex = state.sequence[0] - 1;
    setFocusedIndex(firstIndex);
    setRewardPrizeId(null);
    setIsRunning(true);

    const loop = (now: number) => {
      const state = stateRef.current;

      if (now >= state.nextTickAt) {
        // 현재 스텝의 포커스 업데이트
        const currentIndex = state.sequence[state.stepIdx] - 1;
        setFocusedIndex(currentIndex);
        // 다음 스텝으로 진행
        state.stepIdx++;
        // final 바퀴의 마지막 스텝 완료 시 종료
        if (state.phase === 'final' && state.stepIdx >= numberOfItems) {
          setTimeout(() => {
            const finalIndex = state.rewardPrizeIndex ?? currentIndex;
            const prizeId = prizes[finalIndex].id;
            if (prizeId) setRewardPrizeId(prizeId);
            stop();
          }, animationIntervals.revealDelay);
          return;
        }
        // 한 바퀴 완료 시 다음 바퀴 준비
        if (state.stepIdx >= numberOfItems) {
          state.lap++;
          state.stepIdx = 0;
          if (state.pendingFinal && state.phase !== 'final') {
            // 당첨 바퀴로 전환
            const nextSequence = getShuffledNumbers(numberOfItems);
            const winIndex = state.rewardPrizeIndex ?? 0;
            nextSequence[numberOfItems - 1] = winIndex + 1; // 마지막 칸을 당첨 인덱스로
            state.sequence = nextSequence;
            state.phase = 'final';
            state.pendingFinal = false;
          } else {
            // 일반 바퀴 (2바퀴째부터는 plateau)
            state.sequence = getShuffledNumbers(numberOfItems);
            if (state.lap >= 1) state.phase = 'plateau';
          }
        }
        // 다음 틱 시간 계산
        const interval = computeInterval(state.phase, state.stepIdx);
        state.nextTickAt = now + interval;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [stop, computeInterval, prizes]);

  // 강제 중단 (에러 발생 시)
  const forceStop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setIsRunning(false);
    setFocusedIndex(-1);
    setRewardPrizeId(null);
  }, []);

  const { status, mutateAsync } = useMutation({
    mutationFn: async () => {
      start();
      return postLuckyDrawApi({ eventId: slug, installId: prexInstallationId });
    },
    onSuccess({ data }) {
      setWinner(data);
    },
    onError(error) {
      forceStop();
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          snackbar.error({ message: <FormattedMessage id='Common.r250918.Please_log_in' /> });
          router.push('/signup');
          break;
        case 403:
          snackbar.error({
            message: (
              <FormattedMessage
                id='Campaign.r251124.Your_token_used_refresh_again_multiline'
                values={{ token: tokenName }}
              />
            ),
          });
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  });

  const handleDraw = async () => {
    if (!isLoggedIn) {
      router.push('/signup');
      return;
    }
    await mutateAsync();
  };

  // slug -> tab 이 바뀔때 초기화
  useEffect(() => {
    return () => {
      if (slug) forceStop();
    };
  }, [slug, forceStop]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const isRegistered = luckyDraws?.[0]?.isRegistered;
  const isFinished = luckyDraw?.luckyDrawEventStatus === 'FINISHED';
  const disabled = (isLoggedIn && !isRegistered) || (isLoggedIn && !hasToken) || isFinished;

  return {
    isRunning: isRunning || status === 'pending',
    disabled,
    focusedIndex,
    luckyDrawLayout,
    prizes,
    rewardPrize,
    handleDraw,
  };
};
